import { Box, HStack, ListItem, OrderedList, UnorderedList, VStack } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

import merlinAvatarSrc from 'assets/images/Elise.png';
import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import { useSignIn } from 'components/GitHubButton';
import Icon from 'components/Icon';
import Link from 'components/Link';
import Text from 'components/Text';
import UserAvatar from 'components/UserAvatar';
import { MerlinCardAction, MerlinDiffInfo, MerlinMessage as MerlinMessageType } from 'types/edgeFunctions';

import MerlinLoadingIndicator from './MerlinLoadingIndicator';
import { stripCardCompleteMarker } from './merlinUtils';

const MarkdownContainer = styled.div`
  width: 100%;
  overflow-x: auto;

  p {
    margin-bottom: 8px;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const TAB_LABELS: Record<string, string> = { html: 'HTML', css: 'CSS', js: 'JS' };

const DiffBadges = ({
  diffs,
  onTabSwitch,
}: {
  diffs: MerlinDiffInfo[];
  onTabSwitch?: (tab: 'html' | 'css' | 'js') => void;
}) => (
  <HStack spacing={2} mt={2} flexWrap="wrap">
    {diffs.map((diff) => (
      <Box
        key={diff.tab}
        as="button"
        display="flex"
        alignItems="center"
        gap="8px"
        px={3}
        py={1.5}
        borderRadius="md"
        bg="whiteAlpha.50"
        border="1px solid"
        borderColor="whiteAlpha.100"
        cursor="pointer"
        _hover={{ bg: 'whiteAlpha.100', borderColor: 'whiteAlpha.200' }}
        transition="all 0.15s"
        onClick={() => onTabSwitch?.(diff.tab)}
      >
        <Text fontSize="micro" fontWeight="semibold" opacity="primary">
          {TAB_LABELS[diff.tab] || diff.tab}
        </Text>
        {diff.added > 0 && (
          <Text fontSize="micro" fontWeight="semibold" color="green.400">
            +{diff.added}
          </Text>
        )}
        {diff.removed > 0 && (
          <Text fontSize="micro" fontWeight="semibold" color="red.400">
            -{diff.removed}
          </Text>
        )}
      </Box>
    ))}
  </HStack>
);

const InlineSignInButton = () => {
  const { handleLoginClick } = useSignIn();

  return (
    <Box mt={3}>
      <Button
        onClick={handleLoginClick}
        scheme="gradient"
        size="sm"
        leftIcon={<Icon type="Github" size={16} />}
      >
        Sign in with GitHub
      </Button>
    </Box>
  );
};

const CardCompleteButton = ({
  cardTitle,
  onComplete,
  isCompleted,
  isLoading,
}: {
  cardTitle: string;
  onComplete: () => void;
  isCompleted: boolean;
  isLoading: boolean;
}) => (
  <Box
    mt={3}
    p={3}
    borderRadius="lg"
    bg={isCompleted ? 'whiteAlpha.50' : 'whiteAlpha.50'}
    border="1px solid"
    borderColor={isCompleted ? 'green.700' : 'purple.700'}
    borderLeft="3px solid"
    borderLeftColor={isCompleted ? 'green.400' : 'purple.400'}
    transition="all 0.3s ease"
  >
    {isCompleted ? (
      <HStack spacing={2}>
        <Icon type="CardCheck" size={18} />
        <Text fontSize="small" color="green.300" fontWeight="semibold">
          {cardTitle} completed!
        </Text>
      </HStack>
    ) : (
      <Button
        scheme="gradient"
        size="sm"
        onClick={onComplete}
        isLoading={isLoading}
        leftIcon={<Icon type="GenerateCodeSolution" size={14} />}
      >
        Mark &ldquo;{cardTitle}&rdquo; as Complete
      </Button>
    )}
  </Box>
);

const RemoveSpellButton = ({ onClick }: { onClick: () => void }) => (
  <Box mt={3}>
    <Button
      onClick={onClick}
      scheme="outline"
      size="sm"
      leftIcon={<Icon type="Close" size={14} />}
    >
      Remove Spell
    </Button>
  </Box>
);

interface IMerlinMessageProps {
  message: MerlinMessageType;
  onTabSwitch?: (tab: 'html' | 'css' | 'js') => void;
  onCardComplete?: (cardId: string) => void;
  completedCardIds?: Set<string>;
  onRemoveSpell?: () => void;
}

const MerlinMessage = ({ message, onTabSwitch, onCardComplete, completedCardIds, onRemoveSpell }: IMerlinMessageProps) => {
  const { name, avatarUrl } = useCurrentUser();
  const [isCopied, setIsCopied] = useState<string | null>(null);
  const [isCompletingCard, setIsCompletingCard] = useState(false);

  const isMerlin = message.who === 'Merlin';
  const isStreaming = message.status === 'streaming';
  const isError = message.status === 'error';

  // Use pre-resolved cardAction from message (snapshotted at finalization time)
  const cardAction: MerlinCardAction | null = message.cardAction ?? null;
  // Always strip [CARD_COMPLETE] marker from display text — even without cardAction
  // (AI may output marker for non-project entities where it shouldn't)
  const cleanText = isMerlin && message.message && !isStreaming
    ? stripCardCompleteMarker(message.message)
    : message.message;

  const isCardCompleted = cardAction ? (completedCardIds?.has(cardAction.cardId) ?? false) : false;

  const handleCopyToClipboard = (code: string, blockId: string) => {
    navigator.clipboard.writeText(code);
    setIsCopied(blockId);
    setTimeout(() => setIsCopied(null), 1500);
  };

  return (
    <Box>
      <HStack spacing={3} alignItems="flex-start">
        {isMerlin ? (
          <Box
            minW="32px"
            w="32px"
            h="32px"
            borderRadius="full"
            overflow="hidden"
            bg="purple.800"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <img src={merlinAvatarSrc} alt="Merlin AI" width={32} height={32} />
          </Box>
        ) : (
          <UserAvatar name={name} src={avatarUrl} size="sm" border="none" />
        )}
        <VStack alignItems="flex-start" spacing={1} flex={1} minW={0}>
          {message.status === 'sending' && <MerlinLoadingIndicator />}

          {isError && (
            <Box
              p={2}
              borderRadius="md"
              bg="red.900"
              border="1px solid"
              borderColor="red.700"
              width="100%"
            >
              <Text fontSize="small" color="red.300">
                {message.message || 'Something went wrong. Try again.'}
              </Text>
            </Box>
          )}

          {message.message && !isError && message.status !== 'sending' && (
            <MarkdownContainer>
              <ReactMarkdown
                components={{
                  h1: ({ node, ...props }) => (
                    <Text fontSize="xLarge" fontWeight="semibold" mb={2} {...props} />
                  ),
                  h2: ({ node, ...props }) => (
                    <Text fontSize="large" fontWeight="semibold" mb={1} {...props} />
                  ),
                  h3: ({ node, ...props }) => (
                    <Text fontSize="medium" fontWeight="semibold" mb={1} {...props} />
                  ),
                  ul: ({ node, ordered: _o, ...props }) => <UnorderedList spacing={1} pl={3} {...props} />,
                  ol: ({ node, ordered: _o, ...props }) => <OrderedList spacing={1} pl={3} {...props} />,
                  li: ({ node, ordered: _o, ...props }) => <ListItem {...props} />,
                  a: ({ node, ...props }) => <Link {...props} />,
                  p: ({ node, ...props }) => (
                    <Text lineHeight="24px" fontWeight="regular" {...props} />
                  ),
                  code: ({ node, inline, className, children, ...props }) => {
                    const match = /language-(\w+)/.exec(className || '');
                    const blockId = `code-${Math.random().toString(36).slice(2, 8)}`;

                    return !inline && match ? (
                      <Box position="relative" my={2}>
                        <Box position="absolute" right={2} top={2} zIndex={1}>
                          <Button
                            onClick={() => handleCopyToClipboard(String(children), blockId)}
                            leftIcon={<Icon type="Copy" size={14} />}
                            scheme="outline"
                            size="sm"
                            isDisabled={isStreaming}
                          >
                            <Text fontSize="micro">
                              {isCopied === blockId ? 'Copied!' : 'Copy'}
                            </Text>
                          </Button>
                        </Box>
                        <SyntaxHighlighter
                          className="bds-syntax-highlighter"
                          style={{ ...dracula }}
                          language={match[1]}
                          PreTag="div"
                          {...props}
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      </Box>
                    ) : (
                      <code
                        style={{
                          background: 'rgba(255,255,255,0.1)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '0.9em',
                        }}
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  },
                }}
              >
                {cleanText}
              </ReactMarkdown>
            </MarkdownContainer>
          )}

          {isStreaming && !message.message && !message.isGeneratingCode && (
            <Box
              w="16px"
              h="16px"
              borderRadius="full"
              border="2px solid"
              borderColor="purple.400"
              borderTopColor="transparent"
              animation="spin 0.6s linear infinite"
            />
          )}

          {isStreaming && message.isGeneratingCode && (
            <MerlinLoadingIndicator />
          )}

          {isStreaming && message.streamingTabs && message.streamingTabs.length > 0 && (
            <HStack spacing="6px" mt={1}>
              <Box
                w="12px"
                h="12px"
                borderRadius="full"
                border="2px solid"
                borderColor="purple.400"
                borderTopColor="transparent"
                animation="spin 0.6s linear infinite"
                flexShrink={0}
              />
              <Text fontSize="micro" color="purple.300" fontWeight="semibold">
                Generating code ({message.streamingTabs.map(t => t.toUpperCase()).join(', ')})...
              </Text>
            </HStack>
          )}

          {message.codeChanges && message.codeChanges.length > 0 && !message.hideDiffBadges && (
            <DiffBadges
              diffs={message.diffs || message.codeChanges.map(c => ({ tab: c.tab, added: 0, removed: 0 }))}
              onTabSwitch={onTabSwitch}
            />
          )}

          {message.showSignIn && !isStreaming && <InlineSignInButton />}

          {message.showRemoveSpell && !isStreaming && onRemoveSpell && (
            <RemoveSpellButton onClick={onRemoveSpell} />
          )}

          {cardAction && onCardComplete && (
            <CardCompleteButton
              cardTitle={cardAction.cardTitle}
              isCompleted={isCardCompleted}
              isLoading={isCompletingCard}
              onComplete={async () => {
                setIsCompletingCard(true);
                await onCardComplete(cardAction.cardId);
                setIsCompletingCard(false);
              }}
            />
          )}
        </VStack>
      </HStack>
    </Box>
  );
};

export default MerlinMessage;
